// ./src/components/features/CreateEventWizard/steps/EventTypeStep.tsx
//
// Step 1 — "What are you planning?". The highest-information choice in the wizard: the type decides what the later
// steps OFFER (ROADMAP A2b #62 — Christmas offers Secret Santa, networking Human Bingo…).
//
// Two ways in, one catalog (`eventSetupCatalog`, app-wide):
//   - SEARCH across every type's name, code and synonyms, sub-types included — "xmas" finds Christmas Party under
//     Holiday Party, "cookout" finds BBQ. The owner's ask (2026-09-29): don't grow the list until it overwhelms; let
//     what the host types lead to the type we have.
//   - BROWSE the top-level types by group. A type with sub-types (Holiday Party, Reunion) offers "More specific?"
//     chips once picked — optional, because the parent is a perfectly good answer.
//
// ⚠ Four states, in order: first read in flight → a skeleton; the read FAILED → the error with a retry (never an
// empty list, which would read as "we have no event types"); a search with no match → say so and offer "Other";
// otherwise the list.
//
// #62 P7 — each search is reported ONCE it settles (`wizard_type_search`): the type picked from its results (where it sat,
// and the catalog word that matched — never what was typed), or — after a pause with nothing found — a MISS, with the
// typed words cleaned. The misses are the list of types and words to add.
// ⚠ Not per keystroke: "bir", "birt", "birth" are one search. A miss waits `MISS_SETTLE_MS`, and each term is sent once.
//
// #139 — the QUICK headcount path starts here, above the types: a name, when, and a picture, then the link. It keeps a
// type already picked (else "Other") and goes straight to Basics; tapped again it turns back into the full setup.
// #148 — beside it, the QUICK TICKET SALE: name and when, where, a price and how many, a picture. Tapping one card while
// the other is on moves between them (`toggleQuick` leaves the first, so the full-setup answers are still the ones kept).
// ⚠ Not offered once the event exists — that event was made by the full path, with answers the quick one would drop.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Group, Text, SimpleGrid, Box, TextInput, Button, Chip, Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { FEATURE_CODES } from '@/config';
import { useAppEnvironment, useOptionalEventSetupCatalogContext } from '@/contexts';
import { useCreateGate } from '@/hooks';
import { AppIcon, ErrorAlert, PlanLockNote } from '@/components/generic';
import type { EventSetupType } from '@/utils/api';
import { MIN_TYPE_QUERY } from '@/utils/eventTypeSearch';
import { analytics, WIZARD_EVENTS, wizardTypeSearchParams } from '@/utils/analytics';

import { useEventWizard } from '../WizardContext';
import { WizardSelectCard } from '../components/WizardSelectCard';
import { EVENT_TYPE_GROUPS, EventCategoryGroup } from '../data/eventTypeGroups';
import { toggleQuick } from '../wizardSetup';
import { QUICK_KIND, QuickKind, quickKindOf } from '../wizardTypes';

const SKELETON_TILES = 6;
/** How long a search with no match must sit unchanged before it counts as a miss. */
const MISS_SETTLE_MS = 1500;

export const EventTypeStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { isMobile } = useAppEnvironment();
  const { draft, patchDraft, goToStep, createdEventId } = useEventWizard();
  const catalog = useOptionalEventSetupCatalogContext();

  const [search, setSearch] = useState('');
  const cols = isMobile ? 1 : 2;

  const query = search.trim();
  const results = useMemo(() => (query.length >= MIN_TYPE_QUERY ? (catalog?.search(query) ?? []) : null), [catalog, query]);

  const selected = catalog?.findType(draft.eventCategoryId) ?? null;
  // The top-level type the selection belongs to — itself, or its parent when a sub-type is picked.
  const selectedTop = selected ? (catalog?.parentOf(selected) ?? selected) : null;
  const refinements = selectedTop ? (catalog?.childrenOf(selectedTop.id) ?? []) : [];

  // #62 P7 — searches already reported (a miss by its words, a pick by its type), so each is sent once however often
  // it is re-typed.
  const reportedRef = useRef(new Set<string>());

  const reportSearch = (term: string, matchedType: string | null, matchedWord: string | null, rank: number | null, count: number) => {
    const params = wizardTypeSearchParams({ term, matchedType, matchedWord, rank, results: count });
    const key = `${params?.term ?? ''}|${params?.matched_type}|${params?.matched_word ?? ''}`;

    if (!params || reportedRef.current.has(key)) {
      return;
    }

    reportedRef.current.add(key);
    analytics.sendEvent(WIZARD_EVENTS.TYPE_SEARCH, params);
  };

  const selectType = (type: EventSetupType) => {
    const top = catalog?.parentOf(type) ?? type;

    // A pick made FROM a search (results showing, or "Use Other" after none) reports what the search led to.
    if (results) {
      const index = results.findIndex((r) => r.type.id === type.id);

      reportSearch(query, type.code, index >= 0 ? results[index]!.matchedSynonym : null, index >= 0 ? index + 1 : null, results.length);
    }

    patchDraft({
      eventCategoryId: type.id,
      eventCategoryCode: type.code,
      // A sub-type has no group of its own in the picker — it belongs to its parent's.
      eventCategoryGroup: (top.categoryGroup || type.categoryGroup) as EventCategoryGroup,
    });
  };

  const otherType = catalog?.findType('other') ?? null;

  const quickKind = quickKindOf(draft);
  // #148 — a plan without tickets is said on the sale's card, before the host fills three steps to reach the ticket.
  // ⓘ The note alone, no "See plans" link: the card is one big tap target, and the ticket screen offers the upgrade.
  const ticketGate = useCreateGate(FEATURE_CODES.TICKETING);

  const tapQuick = (kind: QuickKind) => {
    const leaving = quickKind === kind;

    // A type already picked is kept; with none, "Other" (when the catalog has it — the server needs no type at all).
    patchDraft(toggleQuick(draft, otherType, kind));

    if (!leaving) {
      goToStep('basics');
    }
  };

  // A search that found nothing and was left as it is — a miss.
  const missed = !!results && results.length === 0 && !!catalog?.isLoaded;

  useEffect(() => {
    if (!missed) {
      return;
    }

    const timer = window.setTimeout(() => reportSearch(query, null, null, null, 0), MISS_SETTLE_MS);

    return () => {
      window.clearTimeout(timer);
    };
    // ⓘ reportSearch only reads a ref; the term is the trigger.
  }, [missed, query]);

  const descriptionFor = (type: EventSetupType, matchedSynonym: string | null): string | undefined => {
    const parent = catalog?.parentOf(type);
    const parts: string[] = [];

    if (parent) {
      parts.push(t('events:wizard.type.inParent', { defaultValue: 'in {{parent}}', parent: parent.name }));
    }

    if (matchedSynonym) {
      parts.push(t('events:wizard.type.matched', { defaultValue: 'matches “{{term}}”', term: matchedSynonym }));
    }

    if (parts.length === 0 && type.description) {
      parts.push(type.description);
    }

    return parts.length ? parts.join(' · ') : undefined;
  };

  const renderBody = () => {
    // ⚠ The skeleton belongs to the FIRST read only (house rule) — a failed read releases it into the error below.
    if (!catalog || !catalog.isLoaded) {
      return (
        <SimpleGrid cols={cols} spacing="sm">
          {Array.from({ length: SKELETON_TILES }, (_, i) => (
            <Skeleton key={i} height={64} radius="md" />
          ))}
        </SimpleGrid>
      );
    }

    if (catalog.lastError && catalog.types.length === 0) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={catalog.lastError}
            title={t('events:wizard.type.loadFailed', { defaultValue: 'Could not load the event types' })}
            page="Create event — type"
            operation="eventSetupCatalog"
          />
          <Group>
            <Button variant="light" size="md" onClick={() => void catalog.reload()} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (results) {
      if (results.length === 0) {
        return (
          <Stack gap="sm" align="center" py="lg">
            <Text c="dimmed" ta="center" size="md">
              {t('events:wizard.type.noMatches', {
                defaultValue: 'No event type matches “{{query}}”. Pick “Other” — every feature still works.',
                query,
              })}
            </Text>
            {otherType ? (
              <Button variant="light" size="md" onClick={() => selectType(otherType)}>
                {t('events:wizard.type.useOther', { defaultValue: 'Use “Other”' })}
              </Button>
            ) : null}
          </Stack>
        );
      }

      return (
        <SimpleGrid cols={cols} spacing="sm">
          {results.map(({ type, matchedSynonym }) => (
            <WizardSelectCard
              key={type.id}
              title={type.name}
              description={descriptionFor(type, matchedSynonym)}
              emoji={type.emoji ?? undefined}
              selected={draft.eventCategoryId === type.id}
              onClick={() => selectType(type)}
            />
          ))}
        </SimpleGrid>
      );
    }

    return (
      <Stack gap="xl">
        {EVENT_TYPE_GROUPS.map((groupMeta) => {
          const options = catalog.topLevelOfGroup(groupMeta.group);

          if (options.length === 0) {
            return null;
          }

          const refineHere = !!selectedTop && refinements.length > 0 && options.some((o) => o.id === selectedTop.id);

          return (
            <Box key={groupMeta.group}>
              <Group gap="xs" mb="sm" align="flex-start" wrap="nowrap">
                <Box pt={4}>
                  <AppIcon iconName={groupMeta.icon} size={18} />
                </Box>
                <Text fw={600} size="md">
                  {groupMeta.label}
                  <Text span c="dimmed" size="md" fw={400}>
                    {' · '}
                    {groupMeta.description}
                  </Text>
                </Text>
              </Group>
              <SimpleGrid cols={cols} spacing="sm">
                {options.map((option) => (
                  <WizardSelectCard
                    key={option.id}
                    title={option.name}
                    emoji={option.emoji ?? undefined}
                    color={groupMeta.color}
                    selected={selectedTop?.id === option.id}
                    onClick={() => selectType(option)}
                  />
                ))}
              </SimpleGrid>

              {refineHere && selectedTop ? (
                <Stack gap="xs" mt="md">
                  <Text size="md" c="dimmed">
                    {t('events:wizard.type.refine', { defaultValue: 'More specific? (optional)' })}
                  </Text>
                  <Chip.Group
                    multiple={false}
                    value={selected && selected.id !== selectedTop.id ? selected.id : ''}
                    onChange={(value) => {
                      const child = value ? catalog.findType(value as string) : null;

                      selectType(child ?? selectedTop);
                    }}
                  >
                    <Group gap="xs">
                      {refinements.map((child) => (
                        <Chip key={child.id} value={child.id} size="md" variant="light" color={groupMeta.color}>
                          {`${child.emoji ?? ''} ${child.name}`.trim()}
                        </Chip>
                      ))}
                    </Group>
                  </Chip.Group>
                </Stack>
              ) : null}
            </Box>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack gap="lg">
      {!createdEventId ? (
        <SimpleGrid cols={cols} spacing="md">
          <WizardSelectCard
            icon="Hash"
            color="teal"
            title={t('events:wizard.quick.cardTitle', { defaultValue: 'Just need a headcount?' })}
            description={
              quickKind === QUICK_KIND.HEADCOUNT
                ? t('events:wizard.quick.cardOn', { defaultValue: 'Quick setup is on. Tap again to use the full setup instead.' })
                : t('events:wizard.quick.cardDesc', {
                    defaultValue: 'A name, when, and a picture — then a link to share. Guests reply with how many are coming.',
                  })
            }
            selected={quickKind === QUICK_KIND.HEADCOUNT}
            onClick={() => tapQuick(QUICK_KIND.HEADCOUNT)}
          />
          <WizardSelectCard
            icon="Ticket"
            color="grape"
            title={t('events:wizard.quickTickets.cardTitle', { defaultValue: 'Selling tickets?' })}
            description={
              quickKind === QUICK_KIND.TICKETS
                ? t('events:wizard.quick.cardOn', { defaultValue: 'Quick setup is on. Tap again to use the full setup instead.' })
                : t('events:wizard.quickTickets.cardDesc', {
                    defaultValue: 'A name, when and where, a price and how many — then a link to start selling.',
                  })
            }
            selected={quickKind === QUICK_KIND.TICKETS}
            onClick={() => tapQuick(QUICK_KIND.TICKETS)}
            extra={<PlanLockNote verdict={ticketGate.verdict} />}
          />
        </SimpleGrid>
      ) : null}

      <TextInput
        value={search}
        onChange={(event) => {
          // ⚠ Read the value now — never inside a state updater (currentTarget is null by then).
          const value = event.currentTarget.value;

          setSearch(value);
        }}
        placeholder={t('events:wizard.type.searchPlaceholder', {
          defaultValue: 'Search: birthday, BBQ, conference, Christmas…',
        })}
        leftSection={<AppIcon iconName="Search" size={16} />}
        size="md"
        radius="md"
        autoFocus={!isMobile}
        aria-label={t('events:wizard.type.searchLabel', { defaultValue: 'Search event types' })}
      />

      {renderBody()}

      {/* Alternative entry points — wired in a later pass. */}
      <Group justify="center" gap="sm" mt="xs" wrap="wrap">
        <Button variant="subtle" size="md" leftSection={<AppIcon iconName="LayoutGrid" size={16} />} disabled>
          {t('events:wizard.type.fromTemplate', { defaultValue: 'Start from a template' })}
        </Button>
        <Button variant="subtle" size="md" leftSection={<AppIcon iconName="Copy" size={16} />} disabled>
          {t('events:wizard.type.duplicate', { defaultValue: 'Duplicate an event' })}
        </Button>
      </Group>
    </Stack>
  );
};
